import {
  ApiData,
  getEntitiesPerStoreAsync,
  Query,
  ReportQuery,
  ReportResource,
  Result,
  tryTransformToDate,
} from '../Reports';

import { computeAverageSpent } from './helpers';
import {
  AggregatedOrderInfo,
  ApiAggregatedOrdersField,
  ApiOrdersField,
  OrderInfo,
  OrderStatus,
} from './types';

export const mapApiDataToOrderInfo = (apiData: ApiData): OrderInfo => ({
  orderCustomersCount: Number(apiData[ApiOrdersField.OrderCustomersCount]),
  orderTotalTaxIncluded: Number(apiData[ApiOrdersField.OrderTotalTaxIncluded]),
  orderTotalTaxExcluded: Number(apiData[ApiOrdersField.OrderTotalTaxExcluded]),
  orderTotalTaxAmount: Number(apiData[ApiOrdersField.OrderTotalTaxAmount]),
  orderTotalCancelled: Number(apiData[ApiOrdersField.OrderTotalCancelled]),
  orderDiscountAmount: Number(apiData[ApiOrdersField.OrderDiscountAmount]),
  orderTotalDiscount: Number(apiData[ApiOrdersField.OrderTotalDiscount]),
  orderCreatedAt: String(apiData[ApiOrdersField.OrderCreatedAt]),
  orderStatus: apiData[ApiOrdersField.OrderStatus] as OrderStatus,
  tableName: String(apiData[ApiOrdersField.TableName]),
  openingStaffName: String(apiData[ApiOrdersField.OpeningStaffName]),
  closingStaffName: String(apiData[ApiOrdersField.ClosingStaffName] || ''),
  storeId: String(apiData[ApiOrdersField.StoreId]),
  orderName: String(apiData[ApiOrdersField.OrderName]),
  orderClosedAt: String(apiData[ApiOrdersField.OrderClosedAt]),
  orderUuid: String(apiData[ApiOrdersField.OrderUuid]),
  orderOpenedAt: String(apiData[ApiOrdersField.OrderOpenedAt]),
  sumPayments: Number(apiData[ApiOrdersField.SumPayments]),
});

export const mapApiDataToAggregatedOrderInfo = (
  apiData: ApiData,
  _storeId: number,
  { period }: ReportQuery,
): AggregatedOrderInfo => ({
  sumOrderAverageSpentTaxIncluded: computeAverageSpent(
    Number(apiData[ApiAggregatedOrdersField.SumOrderTotalTaxIncluded]),
    Number(apiData[ApiAggregatedOrdersField.SumOrderCustomersCount]),
  ),
  sumOrderAverageSpentTaxExcluded: computeAverageSpent(
    Number(apiData[ApiAggregatedOrdersField.SumOrderTotalTaxExcluded]),
    Number(apiData[ApiAggregatedOrdersField.SumOrderCustomersCount]),
  ),
  sumOrderCustomersCount: Number(apiData[ApiAggregatedOrdersField.SumOrderCustomersCount]),
  sumOrderTotalTaxIncluded: Number(apiData[ApiAggregatedOrdersField.SumOrderTotalTaxIncluded]),
  sumOrderTotalTaxExcluded: Number(apiData[ApiAggregatedOrdersField.SumOrderTotalTaxExcluded]),
  sumOrderTotalTaxAmount: Number(apiData[ApiAggregatedOrdersField.SumOrderTotalTaxAmount]),
  sumOrderTotalCancelled: Number(apiData[ApiAggregatedOrdersField.SumOrderTotalCancelled]),
  sumOrderDiscountAmount: Number(apiData[ApiAggregatedOrdersField.SumOrderDiscountAmount]),
  sumOrderTotalDiscount: Number(apiData[ApiAggregatedOrdersField.SumOrderTotalDiscount]),
  orderStatus: apiData[ApiAggregatedOrdersField.OrderStatus] as OrderStatus | undefined,
  hour: tryTransformToDate(apiData[ApiAggregatedOrdersField.Hour]),
  day: tryTransformToDate(apiData[ApiAggregatedOrdersField.Day]),
  week: tryTransformToDate(apiData[ApiAggregatedOrdersField.Week]),
  sevenDays:
    typeof apiData[ApiAggregatedOrdersField.SevenDays] === 'number'
      ? period.interval.start
          .plus({ week: apiData[ApiAggregatedOrdersField.SevenDays] as number })
          .startOf('day')
      : undefined,
  month: tryTransformToDate(apiData[ApiAggregatedOrdersField.Month]),
  year: tryTransformToDate(apiData[ApiAggregatedOrdersField.Year]),
});

export const getOrdersPerStoreAsync = async (query: Query): Promise<Result<OrderInfo>> =>
  getEntitiesPerStoreAsync<OrderInfo>(
    { ...query, resource: ReportResource.Orders },
    mapApiDataToOrderInfo,
  );

export const getAggregatedOrdersPerStoreAsync = async (
  query: Omit<ReportQuery, 'resource'>,
): Promise<Result<AggregatedOrderInfo>> =>
  getEntitiesPerStoreAsync<AggregatedOrderInfo>(
    { ...query, resource: ReportResource.Orders },
    mapApiDataToAggregatedOrderInfo,
  );
